import React, { FormEvent, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormHelperText,
  Typography,
  TextField,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import * as yup from "yup";
import { ContentWrapper } from "../../components/ContentWrapper";
import { BackButton } from "../../components/BackButton";
import { TopNav } from "../../components/TopNav";
import { DatePicker, TimeField } from "@mui/x-date-pickers";
import { ReunionEvent } from "../../utils/types";
import { addNewEvent, uploadEventImage } from "../../database/queries/commands";
import { FirebaseError } from "firebase/app";

dayjs.extend(customParseFormat);
interface FormState {
  title: string;
  url: string;
  imageUrl: string;
  description: string;
  date: string;
  time: string;
  location: string;
}

type ValidationErrors = {
  title: string;
  url: string;
  imageUrl: string;
  description: string;
  date: string;
  time: string;
  location: string;
};

function EventImagePreview({ imageUrl }: { imageUrl: string }) {
  const [hasImage, setHasImage] = useState(false);

  useEffect(() => {
    if (!imageUrl?.trim()) {
      setHasImage(false);
      return;
    }
    const img = new Image();
    img.onload = () => setHasImage(true);
    img.onerror = () => setHasImage(false);
    img.src = imageUrl;
  }, [imageUrl]);

  if (!hasImage) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        marginTop: 1,
      }}
    >
      <h3>Preview</h3>
      <img
        src={imageUrl}
        alt="Pré-visualização do evento"
        width="100%"
        height={300}
        style={{
          maxHeight: 500,
          objectFit: "contain",
        }}
      />
    </Box>
  );
}

type AddEventProps = {
  embedded?: boolean;
  onSuccess?: () => void;
};

export function AddEvent({ embedded = false, onSuccess }: AddEventProps = {}) {
  const [formState, setFormState] = useState<FormState>({
    title: "",
    url: "",
    imageUrl: "",
    description: "",
    date: "",
    time: "",
    location: "",
  });

  //form validation state
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({
    title: "",
    url: "",
    imageUrl: "",
    description: "",
    date: "",
    time: "",
    location: "",
  });

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [localImagePreview, setLocalImagePreview] = useState<string>("");

  useEffect(() => {
    return () => {
      if (localImagePreview) {
        URL.revokeObjectURL(localImagePreview);
      }
    };
  }, [localImagePreview]);

  const dateValue: Dayjs | null = formState.date
    ? dayjs(formState.date, "DD/MM/YYYY", true)
    : null;
  const timeValue: Dayjs | null = formState.time
    ? dayjs(formState.time, "HH:mm:ss", true)
    : null;

  //form validation schema
  const validationSchema = yup.object().shape({
    title: yup
      .string()
      .required("O título é obrigatório")
      .min(5, "O tamanho mínimo do título é de 5 caracteres")
      .max(50, "O tamanho máximo do título é de 50 caracteres"),
    url: yup.string().required("A URL do evento é obrigatória"),
    imageUrl: yup.string(),
    description: yup
      .string()
      .required("A descrição é obrigatório")
      .min(5, "O tamanho mínimo da descrição é de 5 caracteres")
      .max(1000, "O tamanho máximo da descrição é de 1000 caracteres"),
    location: yup.string().required("A localidade é obrigatória"),
    date: yup.string().required("A data é obrigatória"),
    time: yup.string().required("O horário é obrigatório"),
  });

  const withTimeout = async <T,>(promise: Promise<T>, timeoutMs: number) => {
    return Promise.race([
      promise,
      new Promise<T>((_, reject) =>
        setTimeout(() => reject(new Error("timeout")), timeoutMs),
      ),
    ]);
  };

  const onSubmitHandler = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitSuccess(null);
    try {
      await validationSchema.validate(
        {
          title: formState.title,
          url: formState.url,
          imageUrl: formState.imageUrl,
          description: formState.description,
          location: formState.location,
          date: formState.date,
          time: formState.time,
        },
        { abortEarly: false },
      );
      if (!formState.imageUrl.trim() && !imageFile) {
        setValidationErrors((prev) => ({
          ...prev,
          imageUrl: "Informe uma URL da imagem ou faça upload de um arquivo.",
        }));
        return;
      }

      setIsSaving(true);
      try {
        let finalImageUrl = formState.imageUrl.trim();
        if (imageFile) {
          try {
            finalImageUrl = await withTimeout(uploadEventImage(imageFile), 30000);
          } catch (err: unknown) {
            console.error(err);
            if (err instanceof FirebaseError && err.code === "storage/unauthorized") {
              setSubmitError(
                "Permissão negada no Firebase Storage. Verifique as regras para permitir upload em events/* para usuários autenticados.",
              );
            } else if (err instanceof Error && err.message === "timeout") {
              setSubmitError(
                "O upload da imagem demorou mais do que o esperado. Tente novamente.",
              );
            } else {
              setSubmitError("Falha ao enviar imagem. Tente novamente.");
            }
            return;
          }
        }

        const event = {
          title: formState.title,
          url: formState.url,
          imageUrl: finalImageUrl,
          description: formState.description,
          date: formState.date,
          time: formState.time,
          location: formState.location,
        } as ReunionEvent;

        const id = await withTimeout(addNewEvent(event), 20000);
        setSubmitSuccess(`Evento criado com sucesso (id: ${id}).`);
        setFormState({
          title: "",
          url: "",
          imageUrl: "",
          description: "",
          date: "",
          time: "",
          location: "",
        });
        setImageFile(null);
        setLocalImagePreview("");
        setValidationErrors({
          title: "",
          url: "",
          imageUrl: "",
          description: "",
          date: "",
          time: "",
          location: "",
        });
        onSuccess?.();
      } catch (err: unknown) {
        console.error(err);
        if (err instanceof FirebaseError) {
          if (err.code === "permission-denied") {
            setSubmitError(
              "Permissão negada pelo Firestore. Verifique as regras de segurança do database app-proposta-pe para permitir escrita na coleção events (usuário autenticado).",
            );
          } else {
            setSubmitError(`${err.code}: ${err.message}`);
          }
        } else if (err instanceof Error && err.message === "timeout") {
          setSubmitError(
            "A operação de salvar evento demorou mais do que o esperado. Tente novamente.",
          );
        } else {
          setSubmitError("Não foi possível salvar o evento. Tente novamente.");
        }
      } finally {
        setIsSaving(false);
      }
    } catch (error: yup.ValidationError | unknown) {
      if (error instanceof yup.ValidationError) {
        const errors = Object.fromEntries(
          error.inner.map((err) => [err.path!, err.message]),
        ) as ValidationErrors;
        setValidationErrors(errors);
      } else {
        // Handle other types of errors if needed
        console.error("Unexpected error:", error);
      }
    }
  };

  const formContent = (
    <Box
      component="form"
      onSubmit={onSubmitHandler}
      sx={{
        display: "flex",
        gap: 2,
        paddingBottom: 0,
        paddingLeft: 4,
        paddingRight: 4,
        flexDirection: "column",
        alignItems: "center",
        height: "100%",
      }}
    >
          {submitError && (
            <Alert severity="error" sx={{ width: "100%", maxWidth: 560 }}>
              {submitError}
            </Alert>
          )}
          {submitSuccess && (
            <Alert severity="success" sx={{ width: "100%", maxWidth: 560 }}>
              {submitSuccess}
            </Alert>
          )}
          <FormControl fullWidth>
            <TextField
              required
              label="Título"
              error={Boolean(validationErrors.title)}
              value={formState.title}
              onChange={(e) => {
                e.preventDefault();
                setFormState({ ...formState, title: e.target.value });
              }}
              variant="outlined"
            />
            <FormHelperText>{validationErrors.title}</FormHelperText>
          </FormControl>
          <FormControl fullWidth>
            <TextField
              required
              label="URL do evento"
              error={Boolean(validationErrors.url)}
              value={formState.url}
              onChange={(e) => {
                e.preventDefault();
                setFormState({ ...formState, url: e.target.value });
              }}
              variant="outlined"
            />
            <FormHelperText>{validationErrors.url}</FormHelperText>
          </FormControl>
          <FormControl fullWidth>
            <TextField
              required
              label="Endereço"
              error={Boolean(validationErrors.location)}
              value={formState.location}
              onChange={(e) => {
                e.preventDefault();
                setFormState({ ...formState, location: e.target.value });
              }}
              variant="outlined"
            />
            <FormHelperText>{validationErrors.location}</FormHelperText>
          </FormControl>
          <FormControl fullWidth>
            <DatePicker
              label="Data"
              format="DD/MM/YYYY"
              minDate={dayjs()}
              value={dateValue?.isValid() ? dateValue : null}
              onChange={(e) => {
                if (e) {
                  setFormState({ ...formState, date: e.format("DD/MM/YYYY") });
                } else {
                  setFormState({ ...formState, date: "" });
                }
              }}
            />
            <FormHelperText>{validationErrors.date}</FormHelperText>
          </FormControl>
          <FormControl fullWidth>
            <TimeField
              format="HH:mm"
              label="Horário"
              value={timeValue?.isValid() ? timeValue : null}
              onChange={(e) => {
                if (e) {
                  setFormState({ ...formState, time: e.format("HH:mm:ss") });
                } else {
                  setFormState({ ...formState, time: "" });
                }
              }}
            />
            <FormHelperText>{validationErrors.time}</FormHelperText>
          </FormControl>
          <FormControl fullWidth>
            <TextField
              required
              multiline
              label="Descrição do evento"
              rows={3}
              error={Boolean(validationErrors.description)}
              value={formState.description}
              onChange={(e) => {
                e.preventDefault();
                setFormState({ ...formState, description: e.target.value });
              }}
              variant="outlined"
            />
            <FormHelperText>{validationErrors.description}</FormHelperText>
          </FormControl>
          <FormControl fullWidth>
            <TextField
              required={!imageFile}
              label="URL da imagem"
              error={Boolean(validationErrors.imageUrl)}
              value={formState.imageUrl}
              onChange={(e) => {
                e.preventDefault();
                if (validationErrors.imageUrl) {
                  setValidationErrors({ ...validationErrors, imageUrl: "" });
                }
                setFormState({ ...formState, imageUrl: e.target.value });
              }}
              variant="outlined"
            />
            <Button
              component="label"
              variant="outlined"
              sx={{ marginTop: 1, textTransform: "none", width: "fit-content" }}
            >
              Fazer upload de imagem
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  const selectedFile = e.target.files?.[0] || null;
                  setImageFile(selectedFile);
                  if (selectedFile) {
                    if (localImagePreview) {
                      URL.revokeObjectURL(localImagePreview);
                    }
                    const preview = URL.createObjectURL(selectedFile);
                    setLocalImagePreview(preview);
                    setFormState({ ...formState, imageUrl: "" });
                    setValidationErrors({ ...validationErrors, imageUrl: "" });
                  } else {
                    setLocalImagePreview("");
                  }
                }}
              />
            </Button>
            {imageFile && (
              <Typography variant="body2" sx={{ marginTop: 1 }}>
                Arquivo selecionado: {imageFile.name}
              </Typography>
            )}
            <FormHelperText>{validationErrors.imageUrl}</FormHelperText>
            <EventImagePreview imageUrl={imageFile ? localImagePreview : formState.imageUrl} />
          </FormControl>
          <Button
            variant="contained"
            type="submit"
            disabled={isSaving}
            sx={{
              marginBottom: 2,
              borderRadius: 4,
              padding: 2,
              width: "100%",
              maxWidth: "260px",
              textTransform: "none",
              fontWeight: "bold",
            }}
          >
            {isSaving ? "Salvando…" : "Criar evento"}
          </Button>
    </Box>
  );

  if (embedded) {
    return <>{formContent}</>;
  }

  return (
    <Box style={{ display: "flex", height: "100vh", flexDirection: "column" }}>
      <TopNav leftElement={<BackButton />} label="Criar evento" />
      <ContentWrapper hasScroll>{formContent}</ContentWrapper>
    </Box>
  );
}
